'use client';

import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { ForwardedRef } from 'react';
import {
  CheckboxGroup as RACCheckboxGroup,
  type CheckboxGroupProps as RACCheckboxGroupProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { Stack } from '../spacing/stack/stack.js';
import type { Breakpoint, Responsive, Spacing } from '../spacing/types.js';
import type { Direction, CommonProps, FieldProps } from '../types.js';

export interface CheckboxGroupProps
  extends
    CommonProps,
    FieldProps<string[]>,
    Pick<
      RACCheckboxGroupProps,
      'isDisabled' | 'isReadOnly' | 'isRequired' | 'isInvalid' | 'value' | 'onChange' | 'defaultValue'
    > {
  /** Checkboxes belonging to the group. Each checkbox must have a `value` prop defined. */
  children: ReactNode;
  /**
   * Determines the direction in which checkboxes will be stacked.
   * @default 'vertical'
   */
  direction?: Responsive<Direction>;
}

function CheckboxGroup(
  {
    label,
    description,
    error: errorMessage,
    direction = 'vertical',
    children,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: CheckboxGroupProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('CheckboxGroup requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  return (
    <RACCheckboxGroup
      {...props}
      ref={ref}
      className={clsx('cim-checkbox-group', UNSAFE_className)}
      style={UNSAFE_style}
    >
      <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
        {label}
      </FormFieldLabel>
      <Stack gap={getGapFromDirection(direction)} direction={direction} wrap>
        {children}
      </Stack>
      <FormFieldError>{errorMessage}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>
    </RACCheckboxGroup>
  );
}

/**
 * Allows users to select one or more items from a visible list.
 *
 * See [checkbox usage guidelines](https://ui.cimpress.io/components/checkbox/).
 */
const _CheckboxGroup = withStyleProps(forwardRef(CheckboxGroup), 'CheckboxGroup');

export { _CheckboxGroup as CheckboxGroup };

// Make sure that checkboxes are spaced by 16px when stacked horizontally, and 8px otherwise.
function getGapFromDirection(direction: Responsive<Direction>): Responsive<Spacing> {
  if (typeof direction === 'object') {
    const gap: Responsive<Spacing> = { sm: direction.sm === 'horizontal' ? 16 : 8 };

    (Object.entries(direction) as [Breakpoint, Direction][]).reduce((acc, curr) => {
      gap[curr[0]] = curr[1] === 'horizontal' ? 16 : 8;
      return acc;
    }, gap);

    return gap;
  }

  return direction === 'horizontal' ? 16 : 8;
}
